import { generateThemingVariable } from '../shared-ui-theming-css-variables.service';
import type { SharedUiBackgroundColorOptions, SharedUiColoredBackgroundColorOptions } from './shared-ui-colors.service';
import {
    SharedUiBackgroundColorDerivation,
    SharedUiBackgroundColorName,
    SharedUiColorDerivation,
    type SharedUiColoredBackgroundColorDerivation,
    type SharedUiColorName,
    SharedUiTextColorDerivation
} from './shared-ui-colors.types';

export const DEFAULT_COLOR_DERIVATION = SharedUiColorDerivation.MAIN;
export const DEFAULT_TEXT_DERIVATION = SharedUiTextColorDerivation.MAIN;
export const DEFAULT_BACKGROUND_COLOR = SharedUiBackgroundColorName.LIGHT;
export const DEFAULT_BACKGROUND_DERIVATION = SharedUiBackgroundColorDerivation.MAIN;
export const DEFAULT_COLORED_BACKGROUND_DERIVATION = SharedUiColorDerivation.MAIN;

// Css variable names

function generateColorThemingVariable(variablePath: string[]) {
    return generateThemingVariable(['color', ...variablePath]);
}

// BASIC COLORS

export function generateColorCssVariable(color: SharedUiColorName, derivation: SharedUiColorDerivation = DEFAULT_COLOR_DERIVATION): string {
    if (derivation === DEFAULT_COLOR_DERIVATION) {
        return generateColorThemingVariable([color]);
    } else {
        return generateColorThemingVariable([color, derivation]);
    }
}

// BACKGROUND COLOR

export function generateBackgroundColorCssVariable({
    color = DEFAULT_BACKGROUND_COLOR,
    derivation = DEFAULT_BACKGROUND_DERIVATION
}: SharedUiBackgroundColorOptions = {}): string {
    return generateColorThemingVariable(['bg', generateBackgroundColorName(color, derivation)]);
}

export function generateBackgroundColorClass({
    color = DEFAULT_BACKGROUND_COLOR,
    derivation = DEFAULT_BACKGROUND_DERIVATION
}: SharedUiBackgroundColorOptions = {}): string {
    return `bg-${generateBackgroundColorName(color, derivation)}`;
}

// TEXT COLOR

export function generateTextColorCssVariable(
    derivation: SharedUiTextColorDerivation = DEFAULT_TEXT_DERIVATION,
    { color: backgroundColor = DEFAULT_BACKGROUND_COLOR, derivation: backgroundDerivation = DEFAULT_BACKGROUND_DERIVATION } = {}
): string {
    if (derivation === DEFAULT_TEXT_DERIVATION) {
        return generateColorThemingVariable(['text', 'on', generateBackgroundColorName(backgroundColor, backgroundDerivation)]);
    } else {
        return generateColorThemingVariable(['text', derivation, 'on', generateBackgroundColorName(backgroundColor, backgroundDerivation)]);
    }
}

export function generateTextColorClass(
    derivation: SharedUiTextColorDerivation = DEFAULT_TEXT_DERIVATION,
    {
        color: backgroundColor = DEFAULT_BACKGROUND_COLOR,
        derivation: backgroundDerivation = DEFAULT_BACKGROUND_DERIVATION
    }: SharedUiBackgroundColorOptions = {}
): string {
    return `text-${derivation}-on-${generateBackgroundColorName(backgroundColor, backgroundDerivation)}`;
}

// COLORED TEXT COLOR

export function generateColoredTextColorCssVariable(
    color: SharedUiColorName,
    {
        color: backgroundColor = DEFAULT_BACKGROUND_COLOR,
        derivation: backgroundDerivation = DEFAULT_BACKGROUND_DERIVATION
    }: SharedUiBackgroundColorOptions = {}
): string {
    return generateColorThemingVariable(['text', color, 'on', generateBackgroundColorName(backgroundColor, backgroundDerivation)]);
}

export function generateColoredTextColorClass(
    color: SharedUiColorName,
    {
        color: backgroundColor = DEFAULT_BACKGROUND_COLOR,
        derivation: backgroundDerivation = DEFAULT_BACKGROUND_DERIVATION
    }: SharedUiBackgroundColorOptions = {}
): string {
    return `text-${color}-on-${generateBackgroundColorName(backgroundColor, backgroundDerivation)}`;
}

// COLORED BACKGROUND

export function generateColoredBackgroundColorCssVariable({
    color,
    derivation = DEFAULT_COLOR_DERIVATION
}: SharedUiColoredBackgroundColorOptions): string {
    return generateColorThemingVariable(['bg', generateColoredBackgroundColorName(color, derivation)]);
}

export function generateColoredBackgroundColorClass({
    color,
    derivation = DEFAULT_COLOR_DERIVATION
}: SharedUiColoredBackgroundColorOptions): string {
    return `bg-${generateColoredBackgroundColorName(color, derivation)}`;
}

// TEXT ON COLORED BACKGROUND

export function generateTextOnColoredBackgroundColorCssVariable({
    color,
    derivation = DEFAULT_COLOR_DERIVATION
}: SharedUiColoredBackgroundColorOptions): string {
    return generateColorThemingVariable(['text', 'on', generateColoredBackgroundColorName(color, derivation)]);
}

export function generateTextOnColoredBackgroundColorClass({
    color,
    derivation = DEFAULT_COLOR_DERIVATION
}: SharedUiColoredBackgroundColorOptions): string {
    return `text-on-${generateColoredBackgroundColorName(color, derivation)}`;
}

// STROKE

export function generateStrokeColorCssVariable({
    color = DEFAULT_BACKGROUND_COLOR,
    derivation = DEFAULT_BACKGROUND_DERIVATION
}: SharedUiBackgroundColorOptions = {}): string {
    return generateColorThemingVariable(['stroke', 'on', generateBackgroundColorName(color, derivation)]);
}

// Utils functions

function generateBackgroundColorName(color: SharedUiBackgroundColorName, derivation: SharedUiBackgroundColorDerivation): string {
    if (derivation === DEFAULT_BACKGROUND_DERIVATION) {
        return `${color}`;
    } else {
        return `${color}-${derivation}`;
    }
}

function generateColoredBackgroundColorName(color: SharedUiColorName, derivation?: SharedUiColoredBackgroundColorDerivation): string {
    if (derivation === DEFAULT_COLORED_BACKGROUND_DERIVATION) {
        return `${color}`;
    } else {
        return `${color}-${derivation}`;
    }
}
